<script setup>
  import {computed} from "vue";

  import ComponentStatusBadge from "@components/Badges/StatusBadge.vue";
  import {router} from "@inertiajs/vue3";
  import {recordOriginTexts} from "@pages/Records/Enums/Enums.js";
  import {MedButton, MedLink, MedTooltip} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "PatientinputClientComponentDataSectionTableRow",
  });

  const props = defineProps({
    transfer: {
      type: Object,
      required: true,
    },
  });

  const emit = defineEmits(["startImport", "delete"]);

  function clickRow(event, href) {
    let node = event.target ?? event;

    if (!node.nodeName) {
      return;
    }

    if (node.nodeName.toLowerCase() === "td") {
      // click on <td> => open link
      router.visit(href);
      return;
    }

    if (
      node.nodeName.toLowerCase() === "a" ||
      node.nodeName.toLowerCase() === "button" ||
      node.nodeName.toLowerCase() === "select" ||
      node.nodeName.toLowerCase() === "input" ||
      node.nodeName.toLowerCase() === "textarea" ||
      node.nodeName.toLowerCase() === "dialog"
    ) {
      // click on a clickable element (a or button)
      return;
    }

    if (node.parentNode) {
      // click on any other element: check if parent is <td>
      return clickRow(node.parentNode, href);
    }
  }

  const isAlreadyImported = computed(() => {
    return ["processed", "completed"].includes(props.transfer.status);
  });

  const url = computed(() => {
    return isAlreadyImported.value
      ? route("records.edit", {
          patient: props.transfer.meta.patient_id,
          record: props.transfer.meta.record_id,
        })
      : null;
  });

  const startImport = (event) => {
    if (event !== undefined) {
      let node = event.target ?? event;

      if (!node.nodeName) {
        return;
      }

      if (
        node.nodeName.toLowerCase() === "a" ||
        node.nodeName.toLowerCase() === "button" ||
        node.nodeName.toLowerCase() === "select" ||
        node.nodeName.toLowerCase() === "input" ||
        node.nodeName.toLowerCase() === "textarea" ||
        node.nodeName.toLowerCase() === "dialog"
      ) {
        // click on a clickable element (a or button)
        return;
      }

      if (node.parentNode) {
        // click on any other element: check if parent is <td>
        return startImport(node.parentNode);
      }
    }

    emit("startImport", props.transfer);
  };
</script>

<template>
  <tr
    v-if="transfer.data.masterdata"
    class="cursor-pointer border-b-2 border-gray-200 leading-tight hover:bg-gray-100"
    :data-test="getTestId('patient-row')"
    @click="(event) => (isAlreadyImported && clickRow(event, url)) || (!isAlreadyImported && startImport(event))"
  >
    <td class="px-0.5 py-2">
      <div class="flex flex-row items-center gap-3">
        <template v-if="isAlreadyImported">
          <MedLink as="inertia" :href="url" :data-test="getTestId('table-row-record-edit-button')">
            <span class="text-lg font-semibold text-gray-700">
              {{ transfer.data.masterdata.patient.lastname }},
              {{ transfer.data.masterdata.patient.firstname }}
            </span>
          </MedLink>
        </template>

        <template v-else>
          <span class="text-lg font-semibold text-gray-700">
            {{ transfer.data.masterdata.patient.lastname }},
            {{ transfer.data.masterdata.patient.firstname }}
          </span>
        </template>
      </div>

      <div>
        <MedTooltip content="Geburtsdatum">
          <!-- prettier-ignore -->
          <span class="text-xs">
            {{
              new Date(transfer.data.masterdata.patient.birthdate).toLocaleDateString("de-DE", {dateStyle: "medium"})
            }}<span v-if="transfer.data.masterdata.patient.insurancenumber">,</span>
          </span>
        </MedTooltip>

        <MedTooltip v-if="transfer.data.masterdata.patient.insurancenumber" content="Versichertennummer">
          <span class="text-xs">
            {{ transfer.data.masterdata.patient.insurancenumber }}
          </span>
        </MedTooltip>
      </div>
    </td>

    <td class="px-0.5 py-2">
      <ComponentStatusBadge color="gray" :show-dot="false" :test-id="getTestId('table-row-gray')">
        <template v-if="transfer.meta.is_carer">{{ recordOriginTexts.carerinput }}</template>
        <template v-else>{{ recordOriginTexts.patientinput }}</template>
      </ComponentStatusBadge>
    </td>

    <td class="px-0.5 py-2">
      <ComponentStatusBadge v-if="transfer.status === 'pending'" color="info" :test-id="getTestId('table-row-pending')">
        Noch nicht importiert
      </ComponentStatusBadge>

      <ComponentStatusBadge
        v-else-if="transfer.status === 'processed'"
        color="yellow"
        :test-id="getTestId('table-row-processed')"
      >
        Medikationsanalyse in Arbeit
      </ComponentStatusBadge>

      <ComponentStatusBadge
        v-else-if="transfer.status === 'completed'"
        color="green"
        :test-id="getTestId('table-row-completed')"
      >
        Medikationsanalyse abgeschlossen
      </ComponentStatusBadge>

      <ComponentStatusBadge
        v-else-if="transfer.status === 'rejected'"
        color="red"
        :test-id="getTestId('table-row-rejected')"
      >
        Medikationsanalyse gelöscht
      </ComponentStatusBadge>
    </td>

    <td class="px-0.5 py-2">
      <MedTooltip content="Eingesendet">
        <span>
          {{ transfer.created_at }}
        </span>
      </MedTooltip>
    </td>

    <td class="px-0.5 py-2">
      <div>
        <MedTooltip content="Name">
          <span>
            {{ transfer.data.contactdata?.name }}
          </span>
        </MedTooltip>
      </div>

      <div class="flex w-fit flex-col gap-0.5 text-xs">
        <MedTooltip content="Telefonnummer">
          <span>{{ transfer.data.contactdata?.telephone }}</span>
        </MedTooltip>

        <MedTooltip v-if="transfer.data.contactdata?.email" content="E-Mail">
          <MedLink
            variant="primary"
            :href="'mailto:' + transfer.data.contactdata.email"
            :data-test="getTestId('table-row-email-link')"
          >
            {{ transfer.data.contactdata.email }}
          </MedLink>
        </MedTooltip>
      </div>
    </td>

    <td class="px-0.5 py-2">
      <div class="flex justify-center gap-3">
        <MedTooltip v-if="transfer.status === 'pending'" content="Importieren">
          <MedButton
            aria-label="Fragebogen importieren"
            flat
            icon="install_desktop"
            rounded="full"
            variant="primary"
            :data-test="getTestId('table-row-start-import-button')"
            @click="startImport()"
          />
        </MedTooltip>

        <MedTooltip v-else-if="isAlreadyImported" content="Öffnen">
          <MedButton
            aria-label="Fragebogen öffnen"
            flat
            as="inertia"
            icon="edit"
            rounded="full"
            variant="primary"
            :href="url"
            :data-test="getTestId('table-row-record-edit-button')"
          />
        </MedTooltip>

        <MedTooltip v-else-if="transfer.status === 'rejected'" content="Erneut importieren">
          <MedButton
            aria-label="Fragebogen erneut importieren"
            flat
            icon="install_desktop"
            rounded="full"
            variant="primary"
            :data-test="getTestId('table-row-start-import-button')"
            @click="startImport()"
          />
        </MedTooltip>

        <MedTooltip content="Löschen">
          <MedButton
            aria-label="Fragebogen löschen"
            flat
            icon="delete"
            rounded="full"
            :data-test="getTestId('table-row-delete-button')"
            @click="$emit('delete', transfer)"
          />
        </MedTooltip>
      </div>
    </td>
  </tr>
</template>
